/**
 * Radar Chart Component
 *
 * Displays a 10-quality radar/spider chart for Faana Leadership Qualities.
 * Uses SVG for rendering with optional animation.
 */

import React, { useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { FAANA_DIMENSIONS, DIMENSION_COLORS, QUALITY_SHORT_LABELS } from '../../constants/workbenchConfig';

/**
 * Convert polar coordinates to cartesian
 */
function polarToCartesian(centerX, centerY, radius, angleInDegrees) {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
}

/**
 * Create points for a polygon at given values
 */
function createPolygonPoints(values, centerX, centerY, maxRadius) {
  const angleStep = 360 / values.length;
  return values
    .map((value, index) => {
      const angle = index * angleStep;
      const radius = (value / 100) * maxRadius;
      const point = polarToCartesian(centerX, centerY, radius, angle);
      return `${point.x},${point.y}`;
    })
    .join(' ');
}

/**
 * Radar Chart Component
 */
// eslint-disable-next-line max-lines-per-function
function RadarChart({
  dimensions = {},
  size = 200,
  showLabels = true,
  showPoints = true,
  showGrid = true,
  fillColor = 'rgba(0, 151, 178, 0.3)',
  strokeColor = '#0097B2',
  animated = true,
}) {
  const theme = useTheme();
  const center = size / 2;
  const maxRadius = size * 0.35; // 35% of size for chart area (tighter for 10 vertices)
  const labelRadius = size * 0.45; // Slightly outside chart

  // Build ordered dimension values
  const orderedValues = useMemo(() => {
    return FAANA_DIMENSIONS.map((dim) => dimensions[dim] ?? 50);
  }, [dimensions]);

  // Create polygon points for the data
  const dataPoints = useMemo(() => {
    return createPolygonPoints(orderedValues, center, center, maxRadius);
  }, [orderedValues, center, maxRadius]);

  // Grid rings (25%, 50%, 75%, 100%)
  const gridLevels = [25, 50, 75, 100];

  // Axis lines
  const axisLines = useMemo(() => {
    const angleStep = 360 / FAANA_DIMENSIONS.length;
    return FAANA_DIMENSIONS.map((dim, index) => {
      const angle = index * angleStep;
      const outerPoint = polarToCartesian(center, center, maxRadius, angle);
      return {
        x1: center,
        y1: center,
        x2: outerPoint.x,
        y2: outerPoint.y,
        dimension: dim,
      };
    });
  }, [center, maxRadius]);

  // Label positions
  const labelPositions = useMemo(() => {
    const angleStep = 360 / FAANA_DIMENSIONS.length;
    return FAANA_DIMENSIONS.map((dim, index) => {
      const angle = index * angleStep;
      const pos = polarToCartesian(center, center, labelRadius, angle);
      return {
        ...pos,
        dimension: dim,
        value: orderedValues[index],
        anchor: angle === 0 ? 'middle' : angle < 180 ? 'start' : 'end',
        baseline: angle === 0 ? 'auto' : angle === 180 ? 'hanging' : 'middle',
      };
    });
  }, [center, labelRadius, orderedValues]);

  // Data point positions (for dots)
  const pointPositions = useMemo(() => {
    const angleStep = 360 / FAANA_DIMENSIONS.length;
    return FAANA_DIMENSIONS.map((dim, index) => {
      const angle = index * angleStep;
      const radius = (orderedValues[index] / 100) * maxRadius;
      return {
        ...polarToCartesian(center, center, radius, angle),
        dimension: dim,
        value: orderedValues[index],
      };
    });
  }, [center, maxRadius, orderedValues]);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: size,
        height: size,
      }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Grid rings */}
        {showGrid &&
          gridLevels.map((level) => {
            const ringRadius = (level / 100) * maxRadius;
            const points = FAANA_DIMENSIONS.map((_, index) => {
              const angle = (index * 360) / FAANA_DIMENSIONS.length;
              return polarToCartesian(center, center, ringRadius, angle);
            });
            const pathData =
              points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';
            return (
              <path
                key={`grid-${level}`}
                d={pathData}
                fill="none"
                stroke={theme.palette.divider}
                strokeWidth={0.5}
                strokeDasharray={level === 50 ? '4,2' : undefined}
                opacity={level === 50 ? 0.8 : 0.4}
              />
            );
          })}

        {/* Axis lines */}
        {showGrid &&
          axisLines.map((axis, index) => (
            <line
              key={`axis-${index}`}
              x1={axis.x1}
              y1={axis.y1}
              x2={axis.x2}
              y2={axis.y2}
              stroke={theme.palette.divider}
              strokeWidth={0.5}
              opacity={0.5}
            />
          ))}

        {/* Data polygon */}
        <polygon
          points={dataPoints}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={2}
          style={{
            transition: animated ? 'all 0.5s ease-out' : undefined,
          }}
        />

        {/* Data points */}
        {showPoints &&
          pointPositions.map((point, index) => (
            <circle
              key={`point-${index}`}
              cx={point.x}
              cy={point.y}
              r={4}
              fill={DIMENSION_COLORS[point.dimension] || strokeColor}
              stroke="#fff"
              strokeWidth={1.5}
              style={{
                transition: animated ? 'all 0.3s ease-out' : undefined,
              }}
            />
          ))}

        {/* Labels */}
        {showLabels &&
          labelPositions.map((label, index) => (
            <g key={`label-${index}`}>
              <text
                x={label.x}
                y={label.y - 6}
                textAnchor={label.anchor}
                dominantBaseline={label.baseline}
                fontSize={9}
                fontWeight={500}
                fill={DIMENSION_COLORS[label.dimension] || theme.palette.text.primary}
              >
                {QUALITY_SHORT_LABELS[label.dimension] || label.dimension}
              </text>
              <text
                x={label.x}
                y={label.y + 6}
                textAnchor={label.anchor}
                dominantBaseline={label.baseline}
                fontSize={9}
                fill={theme.palette.text.secondary}
              >
                {label.value}
              </text>
            </g>
          ))}
      </svg>
    </Box>
  );
}

/**
 * Radar Chart with legend
 */
export function RadarChartWithLegend({
  dimensions,
  size = 200,
  title,
  showLegend = true,
  ...props
}) {
  return (
    <Box>
      {title && (
        <Typography
          variant="subtitle2"
          sx={{ textAlign: 'center', mb: 1, fontWeight: 600 }}
        >
          {title}
        </Typography>
      )}
      <RadarChart dimensions={dimensions} size={size} {...props} />
      {showLegend && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            justifyContent: 'center',
            mt: 1,
          }}
        >
          {FAANA_DIMENSIONS.map((dim) => (
            <Box
              key={dim}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: '0.7rem',
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: DIMENSION_COLORS[dim],
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {dim}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

export default RadarChart;
